export interface RevitModelInvalidation {
  eventType: string;
  reason: string;
  documentName: string | null;
  documentPath: string | null;
  modelVersion: number | null;
  /** Plugin-side open generation; advances on every DocumentOpened. */
  generation?: number | null;
  isModified?: boolean | null;
  /**
   * True when every transaction behind a ModelChanged was a validation-repair
   * write (quick fix apply/undo). The validation feature skips these — it
   * refreshes the affected checks itself with a scoped re-run.
   */
  isValidationRepair?: boolean | null;
  previousDocumentPath?: string | null;
}

type RevitModelInvalidator = (event: RevitModelInvalidation) => void;

const invalidators = new Set<RevitModelInvalidator>();
const EmptyDocumentKey = 'no-document';

interface RevitModelInvalidationScope {
  eventType: string;
  documentKey: string;
  modelVersion: number | null;
}

let lastInvalidatedScope: RevitModelInvalidationScope | null = null;

function normalizeDocumentIdentityPart(value: string | null | undefined): string | null {
  const normalized = value?.trim().replace(/\//g, '\\').toLowerCase();
  return normalized ? normalized : null;
}

function resolveDocumentKey(event: Pick<RevitModelInvalidation, 'documentName' | 'documentPath'>): string {
  return normalizeDocumentIdentityPart(event.documentPath) ??
    (normalizeDocumentIdentityPart(event.documentName)
      ? `name:${normalizeDocumentIdentityPart(event.documentName)}`
      : EmptyDocumentKey);
}

function buildInvalidationScope(event: RevitModelInvalidation): RevitModelInvalidationScope {
  return {
    eventType: event.eventType,
    documentKey: resolveDocumentKey(event),
    modelVersion: event.eventType === 'ModelChanged' ? event.modelVersion : null,
  };
}

function shouldPublishInvalidation(event: RevitModelInvalidation, nextScope: RevitModelInvalidationScope): boolean {
  if (!lastInvalidatedScope) {
    return true;
  }

  if (event.eventType === 'ModelChanged') {
    // A null version means the document has no usable version (for example an
    // unsaved project): there is nothing to dedupe on, and swallowing the
    // signal leaves every cache stale for the rest of the session.
    if (nextScope.modelVersion === null) {
      return true;
    }

    return lastInvalidatedScope.documentKey !== nextScope.documentKey ||
      lastInvalidatedScope.modelVersion !== nextScope.modelVersion;
  }

  if (event.eventType === 'DocumentOpened') {
    // A same-path reopen is still a new document generation. The file may also
    // have been upgraded by Revit, changing its available parameter catalog.
    return true;
  }

  if (
    event.eventType === 'DocumentChanged' ||
    event.eventType === 'DocumentClosing' ||
    event.eventType === 'DocumentClosed' ||
    event.eventType === 'DocumentSaved' ||
    event.eventType === 'DocumentSavedAs'
  ) {
    return lastInvalidatedScope.eventType !== nextScope.eventType ||
      lastInvalidatedScope.documentKey !== nextScope.documentKey;
  }

  return true;
}

export function registerRevitModelCacheInvalidator(
  invalidator: RevitModelInvalidator
): () => void {
  invalidators.add(invalidator);
  return () => {
    invalidators.delete(invalidator);
  };
}

function publishInvalidation(event: RevitModelInvalidation, nextScope: RevitModelInvalidationScope): void {
  lastInvalidatedScope = nextScope;
  invalidators.forEach((invalidator) => {
    try {
      invalidator(event);
    } catch (error) {
      console.warn('[modelCacheInvalidation] cache invalidator failed', error);
    }
  });
}

// A null model version gives nothing to dedupe on, and batch writes (parameter
// setup, profile application) commit one transaction per step — the host
// raises ModelChanged for each. Forwarding every one turns a single batch
// into an invalidation storm that exhausts the browser's gRPC budget
// (ERR_INSUFFICIENT_RESOURCES) and stalls the very repair that caused it.
// Coalesce trailing-edge: the latest event goes out once the burst quiets.
const NULL_VERSION_MODEL_CHANGED_COALESCE_MS = 750;
let pendingNullVersionModelChanged: RevitModelInvalidation | null = null;
let nullVersionModelChangedTimer: ReturnType<typeof globalThis.setTimeout> | null = null;

function flushPendingNullVersionModelChanged(): void {
  if (nullVersionModelChangedTimer) {
    globalThis.clearTimeout(nullVersionModelChangedTimer);
    nullVersionModelChangedTimer = null;
  }

  const pending = pendingNullVersionModelChanged;
  pendingNullVersionModelChanged = null;
  if (pending) {
    publishInvalidation(pending, buildInvalidationScope(pending));
  }
}

export function invalidateRevitModelCaches(event: RevitModelInvalidation): void {
  const nextScope = buildInvalidationScope(event);
  if (event.eventType === 'ModelChanged' && nextScope.modelVersion === null) {
    pendingNullVersionModelChanged = event;
    if (nullVersionModelChangedTimer) {
      globalThis.clearTimeout(nullVersionModelChangedTimer);
    }

    nullVersionModelChangedTimer = globalThis.setTimeout(() => {
      nullVersionModelChangedTimer = null;
      const pending = pendingNullVersionModelChanged;
      pendingNullVersionModelChanged = null;
      if (pending) {
        publishInvalidation(pending, buildInvalidationScope(pending));
      }
    }, NULL_VERSION_MODEL_CHANGED_COALESCE_MS);
    return;
  }

  // Immediate events must not overtake a coalesced model change that
  // happened before them (document open/switch/close ordering).
  flushPendingNullVersionModelChanged();
  if (!shouldPublishInvalidation(event, nextScope)) {
    return;
  }

  publishInvalidation(event, nextScope);
}

export function parseRevitModelInvalidationEvent(
  eventType: string,
  payload: string
): RevitModelInvalidation | null {
  if (eventType === 'DocumentSnapshot') {
    // Subscribe-time state push: authoritative active-document identity plus
    // the plugin's open generation. Sent to every (re)connecting client so a
    // UI that was suspended during a close/open cycle can detect it.
    try {
      const parsed = JSON.parse(payload || '{}') as {
        documentName?: string;
        documentPath?: string;
        generation?: number;
      };
      return {
        eventType,
        reason: 'document-snapshot',
        documentName: parsed.documentName?.trim() || null,
        documentPath: parsed.documentPath?.trim() || null,
        modelVersion: null,
        generation: typeof parsed.generation === 'number' ? parsed.generation : null,
      };
    } catch {
      return null;
    }
  }

  if (eventType === 'ModelChanged') {
    try {
      const parsed = JSON.parse(payload || '{}') as {
        documentName?: string;
        documentPath?: string;
        modelVersion?: number;
        validationRepair?: boolean;
        generation?: number;
      };

      return {
        eventType,
        reason: 'model-changed',
        documentName: parsed.documentName?.trim() || null,
        documentPath: parsed.documentPath?.trim() || null,
        modelVersion: typeof parsed.modelVersion === 'number' ? parsed.modelVersion : null,
        isValidationRepair: parsed.validationRepair === true,
        generation: typeof parsed.generation === 'number' ? parsed.generation : null,
      };
    } catch {
      return {
        eventType,
        reason: 'model-changed',
        documentName: payload?.trim() || null,
        documentPath: null,
        modelVersion: null,
      };
    }
  }

  if (
    eventType === 'DocumentOpened' ||
    eventType === 'DocumentChanged' ||
    eventType === 'DocumentClosing' ||
    eventType === 'DocumentClosed' ||
    eventType === 'DocumentSaved' ||
    eventType === 'DocumentSavedAs'
  ) {
    // Plugin now sends JSON {documentName, documentPath} so two documents that
    // share a Title (e.g. an original + its "_detached" copy) stay
    // distinguishable. Fall back to a bare-Title string for older plugin DLLs.
    let documentName: string | null = null;
    let documentPath: string | null = null;
    try {
      const parsed = JSON.parse(payload || '{}') as {
        documentName?: string;
        documentPath?: string;
        previousDocumentPath?: string;
        isModified?: boolean;
        generation?: number;
      };
      documentName = parsed.documentName?.trim() || null;
      documentPath = parsed.documentPath?.trim() || null;
      const reason =
        eventType === 'DocumentClosed'
          ? 'document-closed'
          : eventType === 'DocumentClosing'
            ? 'document-closing'
            : eventType === 'DocumentSaved' || eventType === 'DocumentSavedAs'
              ? 'document-saved'
              : 'document-changed';

      return {
        eventType,
        reason,
        documentName,
        documentPath,
        modelVersion: null,
        isModified: typeof parsed.isModified === 'boolean' ? parsed.isModified : null,
        previousDocumentPath: parsed.previousDocumentPath?.trim() || null,
        generation: typeof parsed.generation === 'number' ? parsed.generation : null,
      };
    } catch {
      documentName = payload?.trim() || null;
      documentPath = null;
    }

    const reason =
      eventType === 'DocumentClosed'
        ? 'document-closed'
        : eventType === 'DocumentClosing'
          ? 'document-closing'
          : eventType === 'DocumentSaved' || eventType === 'DocumentSavedAs'
            ? 'document-saved'
            : 'document-changed';

    return {
      eventType,
      reason,
      documentName,
      documentPath,
      modelVersion: null,
      isModified: null,
      previousDocumentPath: null,
    };
  }

  return null;
}

export function __resetRevitModelInvalidationStateForTests(): void {
  if (nullVersionModelChangedTimer) {
    globalThis.clearTimeout(nullVersionModelChangedTimer);
    nullVersionModelChangedTimer = null;
  }

  pendingNullVersionModelChanged = null;
  lastInvalidatedScope = null;
}
